import { useCallback, useEffect, useMemo, useState } from 'react'
import { navigate, PageProps } from 'gatsby'
import { useBoolean, useDebounce, useDynamicList } from 'ahooks'
import {
  PeriodForSale,
  ProductCreateMutationVariables,
  ProductStatus as ProductStatusType,
  ProductVariantsUpdateInput,
  UnitCode,
  useProductCreateMutation,
} from 'lib/graphql/storeadmin'
import { productsErrorHandling, productVariantsFormat } from 'lib/hooks'
import clsx from 'clsx'
import moment from 'moment'
import Layout from 'components/layouts/tow-column'
import {
  Button,
  Card,
  Checkbox,
  ErrorAlert,
  Form,
  Info,
  SEO,
  showMessage,
  Title,
} from 'components/common'
import {
  AdminOnlyReservation,
  OptionsValue,
  ProductApprovalRequired,
  ProductOptions,
  ProductOptionsInterFace,
  ProductOptionsProps,
  ProductPrice,
  ProductSalePeriod,
  ProductStatusSelect,
  ProductTitleAndDescription,
  ProductVariants,
  SalesStatus,
  Variant,
} from 'components/products'
import { productsDefaultOptions, productsDefaultVariation } from 'lib/constants'
import { routes } from 'config'
import ContactRequiredSettings from 'components/products/inputs/ContactRequiredSettings'
import ChargeUnit from 'components/products/inputs/ChargeUnit'

const onBack = () => navigate('../')

export interface ProductFormValues {
  title: string
  description: string
  // image:
  adminOnlyReservation: boolean
  approvalRequired: boolean
  baseTimeUnit: number
  specialPrice?: number
  price?: number
  inventory?: number
  status: ProductStatusType
  salesStatus?: boolean
  periodForSale?:
    | [moment.Moment | null, moment.Moment | null]
    | PeriodForSale
    | null
  options: string[]
  productsOptions: ProductOptionsInterFace[] | ProductOptionsProps[] | []
  productOptionsValues: OptionsValue[][] | null
  productsVariants?: Variant[]
  params?: Variant[]
  unit?: UnitCode
}

export const Head = () => <SEO title="施設の追加" />

const IndexPage = ({
  params: { id },
}: PageProps<Queries.Query>): JSX.Element => {
  const [ProductCreate, { loading, error }] = useProductCreateMutation()
  const [form] = Form.useForm<ProductFormValues>()
  const [formValues, setFormValues] = useState<ProductFormValues>(null)
  const [storeVariants, setStoreVariants] = useState(null)
  const [checkboxVariants, setCheckboxVariants] = useState(false)
  const [showPeriodOfSales, setPeriodOfSales] = useState(false)
  const [optionsHaveValue, setOptionsHaveValue] = useBoolean(false)
  const [requesting, setRequesting] = useBoolean(false)
  const [haveDuplicateOptionValue, setHaveDuplicateOptionsValue] =
    useState(false)
  const [contactRequiredDates, setContactRequiredDates] = useState<string[]>([])
  //  Products error useState
  const [showProductsErrorAlert, setShowProductsErrorAlert] = useBoolean(false)
  const [productsErrorDescriptions, setProductsErrorDescriptions] = useState<
    string[]
  >([])

  const debounceFormValues = useDebounce(formValues, { wait: 1500 })

  const productVariationsMemo = useMemo(() => {
    const productsOptions = debounceFormValues?.productsOptions

    let editedProductVariants = []

    if (productsOptions?.length > 0) {
      const editedVariants = Array.from(
        new Set(debounceFormValues?.params)
      ).filter((e) => typeof e !== 'undefined')
      editedProductVariants = editedVariants
    }

    const { variants, activeOptionValue, storeVariants } =
      productVariantsFormat({
        productsOptions: productsOptions,
        productsVariants: productsDefaultVariation,
        editedProductVariants: editedProductVariants,
      })
    return { variants, activeOptionValue, storeVariants }
  }, [debounceFormValues])

  useEffect(() => {
    if (checkboxVariants) {
      form.setFieldsValue({
        productsOptions: productsDefaultOptions,
        productsVariants: [],
      })
      setFormValues(null)
    }
  }, [checkboxVariants, form, setFormValues])

  useEffect(() => {
    // Formatting data as Shopify and set data onchange
    const { variants, activeOptionValue, storeVariants } = productVariationsMemo

    if (activeOptionValue?.[0]?.length > 0) {
      setOptionsHaveValue.setTrue()
    }

    if (variants?.length > 0) {
      setStoreVariants(storeVariants)
      form.setFieldsValue({
        productsVariants: variants && variants,
      })
    }
  }, [productVariationsMemo, form, setOptionsHaveValue])

  const { list, remove, getKey, resetList } = useDynamicList<Variant>(
    productVariationsMemo?.variants || []
  )

  const onFinish = async (values: ProductFormValues): Promise<void> => {
    try {
      setRequesting.setTrue()
      const {
        title,
        description,
        specialPrice,
        status,
        salesStatus,
        periodForSale,
        approvalRequired,
        adminOnlyReservation,
        price,
        productsOptions,
        baseTimeUnit,
      } = values

      if (
        typeof periodForSale !== 'undefined' &&
        periodForSale !== null &&
        periodForSale?.[1] <= periodForSale?.[0]
      ) {
        throw new Error('終了日は開始日よりも大きくする必要があります。')
      }
      if (checkboxVariants && productsOptions?.length <= 0) {
        throw new Error('オプションを追加してください')
      }
      if (
        !checkboxVariants &&
        showPeriodOfSales &&
        typeof periodForSale === 'undefined'
      ) {
        throw new Error('売出し期限を確認してください。')
      }

      let productVariants = []
      if (productsOptions?.length > 0) {
        const variants = Array.from(new Set(values?.params)).filter(
          (e) => e !== undefined
        )
        productVariants = variants
      }
      if (productVariants?.length > 100) {
        throw new Error('バリエーションは最大100個までです。')
      }
      const productOptionsNames: string[] =
        productsOptions?.length > 0 &&
        productsOptions?.map((option) => option?.options)

      const variables: ProductCreateMutationVariables = {
        input: {
          name: title,
          description: description,
          status: status,
          baseTimeUnit: baseTimeUnit,
          approvalRequired: approvalRequired,
          adminOnlyReservation: adminOnlyReservation,
          timeBased: true,
          options: productOptionsNames?.length > 0 ? productOptionsNames : [],
          variants:
            productVariants?.length > 0
              ? productVariants?.map<ProductVariantsUpdateInput>((variant) => ({
                  price: Number(variant.price),
                  specialPrice: Number(variant.specialPrice),
                  inventory: 0,
                  availableForSale: Boolean(variant.availableForSale),
                  selectedOptions:
                    variant.selectedOptions?.length > 0 &&
                    variant.selectedOptions?.toString().split(' / '),
                  periodForSale:
                    typeof variant?.periodForSale !== 'undefined' &&
                    variant?.periodForSale !== null
                      ? {
                          start: moment(variant?.periodForSale?.start)?.format(
                            'YYYY-MM-DD'
                          ),
                          end: moment(variant?.periodForSale?.end)?.format(
                            'YYYY-MM-DD'
                          ),
                        }
                      : null,
                  unit: variant.unit,
                }))
              : [
                  {
                    price: Number(price),
                    specialPrice: Number(specialPrice),
                    periodForSale:
                      typeof periodForSale !== 'undefined'
                        ? {
                            start: periodForSale?.[0]?.format('YYYY-MM-DD'),
                            end: periodForSale?.[1]?.format('YYYY-MM-DD'),
                          }
                        : null,
                    inventory: 0,
                    availableForSale: Boolean(salesStatus),
                  },
                ],
          contactRequiredDateList: contactRequiredDates,
        },
      }

      const { data: result } = await ProductCreate({ variables: variables })
      const { productUserErrors } = result.productCreate
      if (productUserErrors.length > 0) {
        setShowProductsErrorAlert.setTrue()
        showMessage('error', 'エラーが発生しました。')
        const productsErrorDescriptions =
          productsErrorHandling(productUserErrors)
        setProductsErrorDescriptions(productsErrorDescriptions)
      } else if (error) {
        throw error
      } else {
        setShowProductsErrorAlert.setFalse()
        showMessage('success', '保存しました。')
        await navigate(`${routes.products}/${result.productCreate.product.id}`)
      }
    } catch (e) {
      setShowProductsErrorAlert.setTrue()
      setProductsErrorDescriptions([e.toString()])
      showMessage(
        'error',
        <>
          <div>施設登録時にエラーが発生しました</div>
          <div>{e.message}</div>
        </>
      )
    } finally {
      setRequesting.setFalse()
    }
  }

  const variantDelete = (list: Variant[], index: number) => {
    const newList = list.filter((_, i) => i !== index)
    const productVariant = form.getFieldValue('productsVariants')
    const productsOptions = form.getFieldValue('productsOptions')
    const uniqueStoredVariants = Array.from(new Set(storeVariants))

    if (newList) {
      const selectedOptions = newList?.map((value, index) =>
        value?.selectedOptions?.toString().split(' / ')
      )

      // Get selected options as an array
      const selectedOptionsArray = []
      selectedOptions.map((array) =>
        array?.forEach((value) => selectedOptionsArray.push(value))
      )

      const uniqueSelectedOptions = Array.from(new Set(selectedOptionsArray))

      let removedField
      if (uniqueSelectedOptions?.length > 0) {
        uniqueStoredVariants.forEach((v) => {
          // checked Removed item & get the item
          if (!uniqueSelectedOptions.includes(v)) {
            removedField = v
          }
        })
      }

      // Stored ProductOptionsValues after delete item
      const removedProductOptionsValues = []

      if (removedField?.length > 0) {
        productsOptions.map((v) =>
          removedProductOptionsValues.push({
            // deleted match item from productOptionsValues
            options: v?.options,
            productOptionsValues: v?.productOptionsValues?.filter(
              (x) => x.optionsValue !== removedField
            ),
          })
        )
      }

      const uniqueRemovedProductOptionsValues = Array.from(
        new Set(removedProductOptionsValues)
      )
      if (
        uniqueRemovedProductOptionsValues?.length > 0 &&
        uniqueRemovedProductOptionsValues[0]?.length !== 0
      ) {
        form.setFieldsValue({
          productsOptions: uniqueRemovedProductOptionsValues,
        })
        setHaveDuplicateOptionsValue(true)
        setStoreVariants(uniqueSelectedOptions)
      }
    }
    if (productVariant?.length === 1) {
      setCheckboxVariants(false)
      setOptionsHaveValue.setFalse()
      setFormValues(null)
    }
  }

  const onOptionDelete = () => {
    const productOptions = form.getFieldValue('productsOptions')
    if (productOptions?.length === 1) {
      setCheckboxVariants(false)
      setOptionsHaveValue.setFalse()
      setFormValues(null)
    }
  }
  const onCheckboxToggle = () => {
    onOptionDelete()
    form.setFieldsValue({
      productsOptions: productsDefaultOptions,
      productsVariants: undefined,
      periodForSale: undefined,
    })
    setPeriodOfSales(!showPeriodOfSales)
    setCheckboxVariants(!checkboxVariants)
  }

  const checkProductOptionExist = useCallback(
    (currentValue) => {
      const productsOptions = form.getFieldValue('productsOptions')
      const productOptionsValues = []
      productsOptions?.forEach((value) => {
        value?.productOptionsValues?.forEach((options) =>
          productOptionsValues.push(options?.optionsValue)
        )
      })
      return productOptionsValues?.filter((data) => data === currentValue)
    },
    [form]
  )

  const onChangeFormValues = useCallback(
    (currentUpdate, values) => {
      const currentOption = currentUpdate?.productsOptions

      let currentChangedOptionsValue
      if (
        typeof currentOption !== 'undefined' &&
        currentOption[currentOption?.length - 1]?.productOptionsValues
      ) {
        currentChangedOptionsValue =
          currentOption[currentOption?.length - 1]?.productOptionsValues[
            currentOption[currentOption?.length - 1]?.productOptionsValues
              .length - 1
          ]?.optionsValue
      }

      if (typeof currentChangedOptionsValue !== 'undefined') {
        const matchValue = checkProductOptionExist(currentChangedOptionsValue)
        if (matchValue?.length < 2) {
          setFormValues(values)
        }
      }
    },
    [checkProductOptionExist]
  )

  return (
    <>
      <Form
        form={form}
        component="form"
        onFinish={onFinish}
        name="newProducts"
        onValuesChange={onChangeFormValues}
      >
        <Layout title="施設を追加する" onBack={onBack}>
          <Layout.Section size="large">
            <Layout.Item>
              {showProductsErrorAlert && (
                <ErrorAlert
                  onCloseAlert={setShowProductsErrorAlert.setFalse}
                  alertDescription={productsErrorDescriptions}
                />
              )}
              <ProductTitleAndDescription />
            </Layout.Item>

            {/* 管理者の承認が必要か？ */}
            <Layout.Item>
              <ProductApprovalRequired isApprovalRequired={false} />
            </Layout.Item>

            {/* 管理者のみ予約可能か？ */}
            <Layout.Item>
              <AdminOnlyReservation isAdminOnlyReservation={false} />
            </Layout.Item>

            {!checkboxVariants && (
              <>
                <Layout.Item>
                  <ProductPrice price={0} specialPrice={0} />
                </Layout.Item>
                <Layout.Item>
                  <Card shadow loading={loading}>
                    <Info
                      title={
                        '販売期間を設定すると、指定された期間中対象の施設を予約できなくなります'
                      }
                    >
                      <Title level={5}>販売期間</Title>
                    </Info>
                    <Checkbox
                      checked={showPeriodOfSales}
                      onChange={() => {
                        form.setFieldsValue({
                          periodForSale: undefined,
                        })
                        setPeriodOfSales((val) => !val)
                      }}
                    >
                      この施設には販売期間があります。
                    </Checkbox>
                    {showPeriodOfSales && <ProductSalePeriod />}
                  </Card>
                </Layout.Item>
                <Layout.Item>
                  <SalesStatus />
                </Layout.Item>
              </>
            )}
            <Layout.Item>
              <ContactRequiredSettings
                loading={loading}
                setContactRequiredDates={setContactRequiredDates}
              />
            </Layout.Item>
          </Layout.Section>

          <Layout.Section size="small">
            <Layout.Item>
              <ProductStatusSelect status={ProductStatusType.Active} />
            </Layout.Item>
            <Layout.Item>
              <ChargeUnit />
            </Layout.Item>
          </Layout.Section>
        </Layout>

        <Layout title="">
          <Layout.Section size="large">
            <Card shadow loading={loading}>
              <Title level={5}>オプション</Title>
              <Checkbox checked={checkboxVariants} onChange={onCheckboxToggle}>
                この施設には複数のオプションがあります。
              </Checkbox>
              {checkboxVariants && (
                <ProductOptions
                  onLastOptionDelete={onOptionDelete}
                  productsOptionsName={[]}
                  productsOptionsValue={[]}
                  haveDuplicateOptionValue={haveDuplicateOptionValue}
                  setHaveDuplicateOptionsValue={setHaveDuplicateOptionsValue}
                />
              )}
            </Card>
          </Layout.Section>
          {checkboxVariants && optionsHaveValue && (
            <Layout.Section size="large">
              <Card shadow loading={loading}>
                <ProductVariants
                  form={form}
                  onVariantDelete={variantDelete}
                  productsVariantsData={productVariationsMemo?.variants || []}
                  dependence={debounceFormValues}
                  list={list}
                  remove={remove}
                  getKey={getKey}
                  resetList={resetList}
                />
              </Card>
            </Layout.Section>
          )}

          <Layout.Footer
            left={
              <Button danger onClick={onBack}>
                キャンセル
              </Button>
            }
            right={
              <div className={clsx('flex', 'justify-end')}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={requesting}
                  disabled={loading}
                >
                  保存する
                </Button>
              </div>
            }
          />
        </Layout>
      </Form>
    </>
  )
}

export default IndexPage
